import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { CategoryCardData, ShellData } from '@/types'
import type { ListingResult } from '@/lib/server/listing'

import { toLocale, useI18n } from '@/lib/i18n'
import { Seo } from '@/components/generals/seo'
import { StoreLayout } from '@/components/store/store-layout'
import { CmsText } from '@/components/store/cms-text'
import { PageHeader } from '@/components/store/page-header'
import { ProductListing } from '@/components/store/product-listing'
import { getCategoryBySlug, loadOffers, loadShell } from '@/lib/server/store'
import { readListingFilters, runListing } from '@/lib/server/listing'

interface CategoryPageProps {
    shell: ShellData
    category: CategoryCardData
    result: ListingResult
}

const CategoryPage: FC<CategoryPageProps> = ({ shell, category, result }): ReactNode => {
    const { tx } = useI18n()

    return (
        <StoreLayout shell={shell}>
            <Seo
                title={category.name.v}
                description={category.description?.v || tx(`تسوق ${category.name.v} بسعر القطاعي أو الجملة مع شحن لكل المحافظات.`, `Shop ${category.name.v} at retail or wholesale prices, shipped to every governorate.`)}
                siteName={shell.settings.storeName.v}
                origin={shell.origin}
                path={`/categories/${category.slug}`}
                ogImage={category.image ?? undefined}
            />

            <PageHeader
                title={<CmsText table='categories' id={category.id} field={category.name} />}
                subtitle={category.description ? <CmsText table='categories' id={category.id} field={category.description} as='p' /> : undefined}
                crumbs={[
                    { label: tx('المنتجات', 'Products'), href: '/products' },
                    { label: <CmsText table='categories' id={category.id} field={category.name} /> }
                ]}
            />

            <div className='bg-surface'>
                <div className='container-page py-10 sm:py-12'>
                    <ProductListing result={result} categories={shell.categories} activeCategoryId={category.id} />
                </div>
            </div>
        </StoreLayout>
    )
}

export const getServerSideProps: GetServerSideProps<CategoryPageProps> = async ({ locale: rawLocale, req, query, params }) => {
    const locale = toLocale(rawLocale)
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const [shell, offers, category] = await Promise.all([loadShell(locale, req), loadOffers(), getCategoryBySlug(slug, locale)])

    if (!category) return { notFound: true }

    const result = await runListing(readListingFilters(query), category.id, locale, offers)

    return { props: { shell, category, result } }
}

export default CategoryPage
